import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Paperclip, Camera, FileText, Trash2, Loader2 } from "lucide-react";

const Thumb = ({ tid, a, onOpen }) => {
  const [src, setSrc] = useState("");
  useEffect(() => {
    if (!a.ct.startsWith("image/")) return undefined;
    let url = "";
    api.get(`/cash-transactions/${tid}/attachments/${a.id}`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [tid, a.id, a.ct]);
  return (
    <button onClick={onOpen} className="h-20 w-20 shrink-0 rounded-lg border bg-slate-50 overflow-hidden flex items-center justify-center" data-testid={`tx-att-open-${a.id}`}>
      {src ? <img src={src} alt={a.nom} className="h-full w-full object-cover" /> : <FileText className="h-7 w-7 text-slate-400" />}
    </button>
  );
};

export function TxAttachments({ t, canManage, onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const atts = t.attachments || [];
  const upload = async (files) => {
    setBusy(true);
    try {
      for (const f of files) { const fd = new FormData(); fd.append("file", f); await api.post(`/cash-transactions/${t.id}/attachments`, fd); }
      toast.success("Justificatif ajouté"); onChange();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  const view = async (a) => { const r = await api.get(`/cash-transactions/${t.id}/attachments/${a.id}`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); };
  const rm = async (a) => { if (!window.confirm("Supprimer ce justificatif ?")) return; try { await api.delete(`/cash-transactions/${t.id}/attachments/${a.id}`); onChange(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <>
      <Button variant="ghost" size="icon" onClick={() => setOpen(true)} className="relative" title="Justificatifs" data-testid={`tx-att-btn-${t.id}`}>
        <Paperclip className={`h-4 w-4 ${atts.length ? "text-brand-600" : "text-slate-300 hover:text-brand-600"}`} />
        {atts.length > 0 && <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-brand-600 text-white text-[10px] leading-4" data-testid={`tx-att-count-${t.id}`}>{atts.length}</span>}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="tx-att-dialog">
          <DialogHeader><DialogTitle className="font-head">Justificatifs — {t.motif}</DialogTitle></DialogHeader>
          {atts.length === 0 ? <p className="text-sm text-slate-500">Aucun justificatif. Ajoutez une photo du reçu ou la facture (PDF).</p> : (
            <div className="space-y-2 max-h-80 overflow-y-auto">{atts.map((a) => (
              <div key={a.id} className="flex items-center gap-3 rounded-lg border p-2" data-testid={`tx-att-${a.id}`}>
                <Thumb tid={t.id} a={a} onOpen={() => view(a)} />
                <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{a.nom}</p>
                  <p className="text-xs text-slate-500">{(a.size / 1024).toFixed(0)} Ko · {a.by} · {new Date(a.created_at).toLocaleDateString("fr-FR")}</p></div>
                {canManage && <Button variant="ghost" size="icon" onClick={() => rm(a)} data-testid={`tx-att-delete-${a.id}`}><Trash2 className="h-4 w-4 text-slate-300 hover:text-rose-500" /></Button>}
              </div>))}</div>)}
          {canManage && <>
            <input ref={input} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => e.target.files.length && upload([...e.target.files])} data-testid="tx-att-input" />
            <Button onClick={() => input.current.click()} disabled={busy} className="w-full bg-brand-600 hover:bg-brand-700 text-white" data-testid="tx-att-add">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Camera className="h-4 w-4 mr-1.5" />}Ajouter une photo ou un PDF</Button>
            <p className="text-[11px] text-slate-400 text-center">Sur téléphone, vous pouvez prendre la photo directement. 10 Mo maximum par fichier.</p>
          </>}
        </DialogContent>
      </Dialog>
    </>
  );
}
